import { getWeatherDataThresholds, updateWeatherDataThresholds } from "../../../Services/EndPoints";
import { getData, putDataWithBody } from "../../../Services/Services";
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import lmsToast from "../Common/CustomToastifier/CustomToastifier";
import "./DashboardTheme.css";

// ═══════════════════════════════════════════════════════════════════════════════
// UI_CONFIG — maps API parameter_name → display icon, unit, weatherData key.
// This is purely presentation mapping (NOT threshold data).
// If a new parameter comes from the API that isn't listed here, it still
// renders with sensible defaults (📊 icon, no unit, kebab-cased key).
// ═══════════════════════════════════════════════════════════════════════════════

const PARAMETER_UI_CONFIG = {
  "Temperature": { icon: "🌡️", unit: "°C", key: "temperature" },
  "Humidity": { icon: "💧", unit: "%", key: "humidity" },
  "Wind Speed": { icon: "🌬️", unit: "km/h", key: "windspeed" },
  "Wind Direction": { icon: "🧭", unit: "", key: "winddirection" },
  "Rain Probability": { icon: "🌧️", unit: "%", key: "rain" },
  "Precipitation": { icon: "⛈️", unit: "%", key: "thunder" },
  "Visibility": { icon: "👁️", unit: "km", key: "visibility" },
  "Pressure": { icon: "📉", unit: "hPa", key: "pressure" },
  "UV Index": { icon: "☀️", unit: "", key: "uv" },
  "Dew Point": { icon: "🌫️", unit: "°C", key: "dewpoint" },
};

// Severity style palette (reused for badges & value pills)
const SEVERITY_STYLES = {
  extreme: { color: "#EF4444", bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.25)" },
  high: { color: "#F97316", bg: "rgba(249,115,22,0.08)", border: "rgba(249,115,22,0.25)" },
  moderate: { color: "#F59E0B", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.25)" },
  low: { color: "#10B981", bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.25)" },
};

const SEVERITY_BADGE_CONFIG = {
  extreme: { label: "ALERT", bg: "linear-gradient(135deg, #DC2626, #EF4444)", color: "#fff", shadow: "0 2px 8px rgba(239,68,68,0.4)", pulse: true },
  high: { label: "ALERT", bg: "linear-gradient(135deg, #EA580C, #F97316)", color: "#fff", shadow: "0 2px 8px rgba(249,115,22,0.35)", pulse: true },
  moderate: { label: "WARNING", bg: "linear-gradient(135deg, #D97706, #F59E0B)", color: "#fff", shadow: "0 2px 8px rgba(245,158,11,0.3)", pulse: false },
  low: { label: "INFO", bg: "linear-gradient(135deg, #059669, #10B981)", color: "#fff", shadow: "0 2px 6px rgba(16,185,129,0.25)", pulse: false },
};


// ═══════════════════════════════════════════════════════════════════════════════
// buildParametersFromApi — transforms GET-all API response into a usable list.
// Each item becomes { apiId, parameterName, icon, unit, weatherKey, valueHigh,
// valueLow, category, isNumeric, rawValueHigh, rawValueLow }.
// Non-numeric entries (e.g. Wind Direction = "North") are included for the
// config modal but skipped during alert generation.
// ═══════════════════════════════════════════════════════════════════════════════

const buildParametersFromApi = (apiData) => {
  const validData = Array.isArray(apiData) ? apiData.filter((item) => !item.is_deleted) : [];

  if (validData.length === 0) {
    // Fallback to default list if no thresholds are configured for this state yet
    return Object.keys(PARAMETER_UI_CONFIG).map((name) => {
      const ui = PARAMETER_UI_CONFIG[name];
      return {
        apiId: null,
        parameterName: name,
        icon: ui.icon || "📊",
        unit: ui.unit ?? "",
        weatherKey: ui.key || name.toLowerCase().replace(/\s+/g, ""),
        valueHigh: null,
        valueLow: null,
        rawValueHigh: "",
        rawValueLow: "",
        category: "high",
        isNumeric: true,
      };
    });
  }

  return validData
    .map((item) => {
      const name = item.parameter_name || "";
      const ui = PARAMETER_UI_CONFIG[name] || {};

      const rawHigh = item.value_high;
      const rawLow = item.value_low;

      const numericHigh =
        rawHigh != null && rawHigh !== "" && !isNaN(parseFloat(rawHigh))
          ? parseFloat(rawHigh)
          : null;
      const numericLow =
        rawLow != null && rawLow !== "" && !isNaN(parseFloat(rawLow))
          ? parseFloat(rawLow)
          : null;

      return {
        apiId: item.id,
        parameterName: name,
        icon: ui.icon || "📊",
        unit: ui.unit ?? "",
        weatherKey: ui.key || name.toLowerCase().replace(/\s+/g, ""),
        valueHigh: numericHigh,
        valueLow: numericLow,
        rawValueHigh: rawHigh ?? "",
        rawValueLow: rawLow ?? "",
        category: (item.category || "HIGH").toLowerCase(),
        isNumeric: numericHigh !== null || numericLow !== null,
      };
    });
};


// ═══════════════════════════════════════════════════════════════════════════════
// computeSeverity — determines severity from how much the value exceeds / falls
// below its threshold. Fully dynamic, no hardcoded ranges per parameter.
// ═══════════════════════════════════════════════════════════════════════════════

const computeSeverity = (value, threshold, direction = "high") => {
  if (threshold == null || threshold === 0) return "moderate";

  const ratio =
    direction === "high"
      ? (value - threshold) / Math.abs(threshold)
      : (threshold - value) / Math.abs(threshold);

  if (ratio >= 0.25) return "extreme";
  if (ratio >= 0.10) return "high";
  return "moderate";
};


// ═══════════════════════════════════════════════════════════════════════════════
// getValueColor — returns a severity color for any current value relative
// to its threshold(s). Used in the config modal "Current" column.
// ═══════════════════════════════════════════════════════════════════════════════

const getValueColor = (value, param) => {
  if (value == null) return SEVERITY_STYLES.low.color;

  if (param.valueHigh != null && value >= param.valueHigh) {
    const sev = computeSeverity(value, param.valueHigh, "high");
    return SEVERITY_STYLES[sev].color;
  }
  if (param.valueLow != null && value <= param.valueLow) {
    const sev = computeSeverity(value, param.valueLow, "low");
    return SEVERITY_STYLES[sev].color;
  }

  // Approaching threshold
  if (param.valueHigh != null && value >= param.valueHigh * 0.8)
    return SEVERITY_STYLES.moderate.color;
  if (param.valueLow != null && param.valueLow > 0 && value <= param.valueLow * 1.2)
    return SEVERITY_STYLES.moderate.color;

  return SEVERITY_STYLES.low.color;
};

const getSeverityBg = (color) => {
  const entry = Object.values(SEVERITY_STYLES).find((s) => s.color === color);
  return entry ? entry.bg : SEVERITY_STYLES.low.bg;
};


// ═══════════════════════════════════════════════════════════════════════════════
// formatValue — safe number formatting
// ═══════════════════════════════════════════════════════════════════════════════

const formatValue = (val) => {
  if (val == null) return "—";
  return Number.isInteger(val) ? String(val) : val.toFixed(1);
};


// ═══════════════════════════════════════════════════════════════════════════════
// generateWeatherAlerts — fully dynamic, works on API-built parameters list.
// No static threshold array, no default values.
// ═══════════════════════════════════════════════════════════════════════════════

export const generateWeatherAlerts = (weatherData, capitalName, stateName, parameters, currentTime) => {
  if (!weatherData || Object.keys(weatherData).length === 0) return [];
  if (!parameters || parameters.length === 0) return [];

  const dateObj = currentTime ? new Date(currentTime) : new Date();
  const formattedDate = dateObj.toLocaleDateString("en-GB", {
    timeZone: "Asia/Calcutta",
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).replace(/\//g, "-");

  const formattedTime = dateObj.toLocaleTimeString("en-US", {
    timeZone: "Asia/Calcutta",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).toUpperCase();

  const timeStr = `${formattedDate} ${formattedTime}`;

  const alerts = [];

  parameters.forEach((param) => {
    if (!param.isNumeric) return;

    const value = weatherData[param.weatherKey];
    if (value == null) return;

    let breached = false;
    let direction = "high";
    let threshold = null;
    let alertLabel = "";
    let alertIcon = param.icon;

    let thresholdValueStr = "";
    let currentValueStr = `${formatValue(value)}${param.unit ? ` ${param.unit}` : ""}`;

    // Check HIGH threshold breach
    if (param.valueHigh != null && value >= param.valueHigh) {
      breached = true;
      direction = "high";
      threshold = param.valueHigh;

      const isTemp = param.parameterName?.toLowerCase().includes("temperature");
      alertLabel = isTemp ? "Heat Wave" : `High ${param.parameterName} Alert`;
      if (isTemp) alertIcon = "🔥";
      thresholdValueStr = `≥ ${param.valueHigh}${param.unit ? ` ${param.unit}` : ""}`;
    }
    // Check LOW threshold breach
    else if (param.valueLow != null && value <= param.valueLow) {
      breached = true;
      direction = "low";
      threshold = param.valueLow;

      const isTemp = param.parameterName?.toLowerCase().includes("temperature");
      alertLabel = isTemp ? "Cold Wave" : `Low ${param.parameterName} Alert`;
      if (isTemp) alertIcon = "❄️";
      thresholdValueStr = `≤ ${param.valueLow}${param.unit ? ` ${param.unit}` : ""}`;
    }

    if (!breached) return;

    const severity = computeSeverity(value, threshold, direction);
    const styles = SEVERITY_STYLES[severity] || SEVERITY_STYLES.moderate;

    alerts.push({
      id: `${param.weatherKey}-${Date.now()}`,
      parameterId: param.weatherKey,
      parameterName: param.parameterName,
      icon: alertIcon,
      label: alertLabel,
      thresholdValue: thresholdValueStr,
      currentValue: currentValueStr,
      value,
      unit: param.unit,
      location: capitalName || stateName || "—",
      time: timeStr,
      severity,
      severityColor: styles.color,
      bg: styles.bg,
      border: styles.border,
    });
  });

  const severityOrder = { extreme: 0, high: 1, moderate: 2, low: 3 };
  alerts.sort(
    (a, b) => (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9)
  );
  return alerts;
};


// ═══════════════════════════════════════════════════════════════════════════════
// ThresholdConfigModal — built entirely from API-fetched parameters
// ═══════════════════════════════════════════════════════════════════════════════

const ThresholdConfigModal = ({
  isOpen,
  onClose,
  parameters,
  savedParameters,
  onSave,
  weatherData,
  stateId,
}) => {
  // Draft keyed by parameterName → { high?, low?, rawHigh?, rawLow? }
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const modalRef = useRef(null);

  // ─── Validation helper ───────────────────────────────────────────────
  // Returns an error object keyed by parameterName → error message string.
  // Only validates rows where BOTH high and low are filled with valid numbers.
  const validateDraft = useCallback((currentDraft, params) => {
    const errors = {};
    params.forEach((p) => {
      if (!p.isNumeric) return;
      const d = currentDraft[p.parameterName] || {};
      const highVal = d.high;
      const lowVal = d.low;

      const hasHigh = highVal !== undefined && highVal !== "" && !isNaN(Number(highVal));
      const hasLow = lowVal !== undefined && lowVal !== "" && !isNaN(Number(lowVal));

      if (hasHigh && hasLow && Number(highVal) <= Number(lowVal)) {
        errors[p.parameterName] = "High must be greater than Low";
      }
    });
    return errors;
  }, []);

  // Build draft from parameters when modal opens
  useEffect(() => {
    if (isOpen && parameters.length > 0) {
      const d = {};
      parameters.forEach((p) => {
        d[p.parameterName] = {
          ...(p.valueHigh != null && { high: p.valueHigh }),
          ...(p.valueLow != null && { low: p.valueLow }),
          ...(p.rawValueHigh && !p.isNumeric && { rawHigh: p.rawValueHigh }),
          ...(p.rawValueLow && !p.isNumeric && { rawLow: p.rawValueLow }),
        };
      });
      setDraft(d);
      setValidationErrors({});
      setSaveStatus(null);
    }
  }, [isOpen, parameters]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleChange = (paramName, field, value) => {
    const numVal = value === "" ? "" : parseFloat(value);
    setDraft((prev) => {
      const next = {
        ...prev,
        [paramName]: { ...prev[paramName], [field]: numVal },
      };
      // Re-validate on every change so errors appear / disappear in real-time
      setValidationErrors(validateDraft(next, parameters));
      return next;
    });
  };

  const getSeverityCategory = (paramName) => {
    const param = parameters.find((p) => p.parameterName === paramName);
    if (!param || !param.isNumeric) return "HIGH";
    const currentVal = weatherData?.[param.weatherKey];
    if (currentVal == null) return "HIGH";

    if (param.valueHigh != null && currentVal >= param.valueHigh) {
      return computeSeverity(currentVal, param.valueHigh, "high").toUpperCase();
    }
    if (param.valueLow != null && currentVal <= param.valueLow) {
      return computeSeverity(currentVal, param.valueLow, "low").toUpperCase();
    }
    return "HIGH";
  };

  // Computed flag: true when ANY row has a validation error
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const handleSave = async () => {
    // Final validation check before saving
    const errors = validateDraft(draft, parameters);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSaving(true);
    setSaveStatus(null);

    try {
      const requestData = parameters.map((p) => {
        const d = draft[p.parameterName] || {};
        return {
          id: p.apiId,
          parameterName: p.parameterName,
          valueHigh:
            d.high != null && d.high !== "" ? Number(d.high) : (p.valueHigh ?? ""),
          valueLow:
            d.low != null && d.low !== "" ? Number(d.low) : (p.valueLow ?? ""),
          state_id: stateId,
          category: getSeverityCategory(p.parameterName),
        };
      });

      const response = await putDataWithBody(updateWeatherDataThresholds, requestData, {});

      if (response && response.status === "success") {
        lmsToast({
          toasterType: "success",
          msg: response.msg || "Weather parameters updated successfully.",
          customConfig: {
            style: {
              background: "#f0f7ff",
              color: "#0341a5",
              fontWeight: "600",
              fontSize: "14px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            },
            hideProgressBar: false,
            progressStyle: { background: "#0341a5" },
          },
        });
      }

      setSaveStatus("success");
      onSave(draft);
      setTimeout(() => {
        setSaving(false);
        setSaveStatus(null);
        onClose();
      }, 800);
    } catch (err) {
      console.error("Failed to save thresholds:", err);
      setSaveStatus("error");
      setSaving(false);
      onSave(draft);
    }
  };

  // Reset to last-saved API values
  const handleReset = () => {
    const d = {};
    savedParameters.forEach((p) => {
      d[p.parameterName] = {
        ...(p.valueHigh != null && { high: p.valueHigh }),
        ...(p.valueLow != null && { low: p.valueLow }),
        ...(p.rawValueHigh && !p.isNumeric && { rawHigh: p.rawValueHigh }),
        ...(p.rawValueLow && !p.isNumeric && { rawLow: p.rawValueLow }),
      };
    });
    setDraft(d);
    setValidationErrors({});
  };

  // Saved draft for "modified" comparison
  const savedDraft = useMemo(() => {
    const d = {};
    savedParameters.forEach((p) => {
      d[p.parameterName] = {
        ...(p.valueHigh != null && { high: p.valueHigh }),
        ...(p.valueLow != null && { low: p.valueLow }),
        ...(p.rawValueHigh && !p.isNumeric && { rawHigh: p.rawValueHigh }),
        ...(p.rawValueLow && !p.isNumeric && { rawLow: p.rawValueLow }),
      };
    });
    return d;
  }, [savedParameters]);

  const isModified = JSON.stringify(draft) !== JSON.stringify(savedDraft);

  if (!isOpen) return null;

  // Compact inline input
  const renderInlineInput = (paramName, field, unit) => {
    const val = draft[paramName]?.[field];
    const savedVal = savedDraft[paramName]?.[field];
    const changed = val !== undefined && val !== "" && val !== savedVal;
    const hasError = !!validationErrors[paramName];
    // Determine if THIS specific field is contributing to the error
    const isErrorField = hasError;
    return (
      <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <input
          type="number"
          step="any"
          value={val ?? ""}
          placeholder={savedVal != null ? String(savedVal) : "—"}
          onChange={(e) => handleChange(paramName, field, e.target.value)}
          style={{
            width: 95,
            height: 34,
            padding: "0 8px",
            paddingRight: unit ? 32 : 8,
            border: isErrorField
              ? "1.5px solid #EF4444"
              : changed
                ? "1.5px solid #60a5fa"
                : "1.5px solid #e2e8f0",
            borderRadius: 7,
            fontSize: 13,
            fontWeight: 600,
            color: isErrorField ? "#991B1B" : "var(--od-text-primary)",
            background: isErrorField
              ? "#FEF2F2"
              : changed
                ? "#eff6ff"
                : "#f8fafc",
            outline: "none",
            transition: "all 0.15s",
            boxSizing: "border-box",
            textAlign: "center",
          }}
          onFocus={(e) => {
            if (isErrorField) {
              e.target.style.borderColor = "#EF4444";
              e.target.style.boxShadow = "0 0 0 3px rgba(239,68,68,0.15)";
            } else {
              e.target.style.borderColor = "#60a5fa";
              e.target.style.boxShadow = "0 0 0 3px rgba(96,165,250,0.15)";
            }
          }}
          onBlur={(e) => {
            if (isErrorField) {
              e.target.style.borderColor = "#EF4444";
              e.target.style.boxShadow = "none";
            } else {
              e.target.style.borderColor = changed ? "#60a5fa" : "var(--od-border)";
              e.target.style.boxShadow = "none";
            }
          }}
        />
        {unit && (
          <span
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 10,
              color: isErrorField ? "#F87171" : "var(--od-text-muted)",
              fontWeight: 600,
              pointerEvents: "none",
            }}
          >
            {unit}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "tcFadeIn 0.15s ease-out",
      }}
    >
      <div
        ref={modalRef}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "82vh",
          background: "var(--od-card-bg)",
          borderRadius: 14,
          boxShadow: "0 24px 48px -12px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.05)",
          display: "flex",
          flexDirection: "column",
          animation: "tcSlideUp 0.2s ease-out",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--od-border)",
            background: "linear-gradient(to bottom, var(--od-card-bg), var(--od-surface-alt))",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 18 }}>⚙️</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--od-text-primary)", flex: 1 }}>
            Weather Thresholds
          </span>
          {isModified && !hasValidationErrors && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#f59e0b",
                background: "#fefce8",
                border: "1px solid #fde68a",
                padding: "2px 8px",
                borderRadius: 4,
              }}
            >
              Unsaved changes
            </span>
          )}
          {hasValidationErrors && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#EF4444",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                padding: "2px 8px",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
            >
              ⚠ Validation error
            </span>
          )}
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "none",
              background: "var(--od-surface-alt)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              color: "var(--od-text-muted)",
              transition: "all 0.12s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--od-border)";
              e.currentTarget.style.color = "var(--od-text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--od-surface-alt)";
              e.currentTarget.style.color = "var(--od-text-muted)";
            }}
          >
            ✕
          </button>
        </div>

        {/* Table Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 20px",
            borderBottom: "1px solid var(--od-border)",
            background: "var(--od-surface-alt)",
            gap: 12,
          }}
        >
          <span
            style={{
              flex: 1,
              fontSize: 10,
              fontWeight: 700,
              color: "var(--od-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Parameter
          </span>
          <span
            style={{
              width: 90,
              fontSize: 10,
              fontWeight: 700,
              color: "var(--od-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              textAlign: "center",
            }}
          >
            Current
          </span>
          <span
            style={{
              width: 100,
              fontSize: 10,
              fontWeight: 700,
              color: "var(--od-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              textAlign: "center",
            }}
          >
            High ≥
          </span>
          <span
            style={{
              width: 100,
              fontSize: 10,
              fontWeight: 700,
              color: "var(--od-text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              textAlign: "center",
            }}
          >
            Low ≤
          </span>
        </div>

        {/* Rows — dynamically from API parameters */}
        <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "thin" }}>
          {parameters.map((param, idx) => {
            const isLast = idx === parameters.length - 1;
            const currentVal = weatherData?.[param.weatherKey];
            const valColor = getValueColor(currentVal, param);
            const rowError = validationErrors[param.parameterName];

            return (
              <div
                key={param.apiId ?? param.parameterName}
                style={{
                  borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 20px",
                    paddingBottom: rowError ? 4 : 10,
                    gap: 12,
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--od-surface-alt)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {/* Parameter name */}
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 16,
                        flexShrink: 0,
                        width: 22,
                        textAlign: "center",
                      }}
                    >
                      {param.icon}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "var(--od-text-primary)",
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {param.parameterName}
                      </div>
                      {param.unit && (
                        <div style={{ fontSize: 13, color: "var(--od-text-muted)", lineHeight: 1.2 }}>
                          {param.unit}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Current value */}
                  <div style={{ width: 90, display: "flex", justifyContent: "center" }}>
                    {currentVal == null ? (
                      <span style={{ fontSize: 11, color: "var(--od-text-muted)" }}>—</span>
                    ) : (
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: valColor,
                          background: getSeverityBg(valColor),
                          padding: "3px 10px",
                          borderRadius: 6,
                          border: `1px solid ${valColor}22`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatValue(currentVal)}
                        {param.unit ? ` ${param.unit}` : ""}
                      </span>
                    )}
                  </div>

                  {/* High threshold */}
                  <div style={{ width: 100, display: "flex", justifyContent: "center" }}>
                    {param.isNumeric ? (
                      renderInlineInput(
                        param.parameterName,
                        "high",
                        param.unit
                      )
                    ) : (
                      <span style={{ fontSize: 11, color: "var(--od-text-muted)" }}>
                        {param.rawValueHigh || "—"}
                      </span>
                    )}
                  </div>

                  {/* Low threshold */}
                  <div style={{ width: 100, display: "flex", justifyContent: "center" }}>
                    {param.isNumeric ? (
                      renderInlineInput(
                        param.parameterName,
                        "low",
                        param.unit
                      )
                    ) : (
                      <span style={{ fontSize: 11, color: "var(--od-text-muted)" }}>
                        {param.rawValueLow || "—"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Inline validation error message */}
                {rowError && (
                  <div
                    style={{
                      padding: "0 20px 8px 50px",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      animation: "tcErrorSlide 0.2s ease-out",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5" />
                      <path d="M8 4.5V9" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="8" cy="11.5" r="0.75" fill="#EF4444" />
                    </svg>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#EF4444",
                        lineHeight: 1,
                      }}
                    >
                      {rowError}
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {parameters.length === 0 && (
            <div
              style={{
                padding: "24px 20px",
                textAlign: "center",
                color: "var(--od-text-muted)",
                fontSize: 13,
              }}
            >
              No threshold parameters found. Contact your administrator.
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--od-border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            onClick={handleReset}
            disabled={!isModified || saving}
            style={{
              padding: "7px 14px",
              borderRadius: 7,
              border: "1px solid var(--od-border)",
              background: "var(--od-card-bg)",
              fontSize: 12,
              fontWeight: 500,
              color: isModified && !saving ? "var(--od-text-secondary)" : "#cbd5e1",
              cursor: isModified && !saving ? "pointer" : "default",
              transition: "all 0.12s",
            }}
            onMouseEnter={(e) => {
              if (isModified && !saving) {
                e.currentTarget.style.background = "var(--od-surface-alt)";
                e.currentTarget.style.borderColor = "var(--od-border)";
              }
            }}
            onMouseLeave={(e) => {
              if (isModified && !saving) {
                e.currentTarget.style.background = "var(--od-card-bg)";
                e.currentTarget.style.borderColor = "var(--od-border)";
              }
            }}
          >
            Reset
          </button>

          <div style={{ flex: 1 }}>
            {saveStatus === "error" && (
              <span style={{ fontSize: 11, color: "#EF4444", fontWeight: 600 }}>
                ⚠️ Failed to save. Changes applied locally.
              </span>
            )}
          </div>

          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "7px 16px",
              borderRadius: 7,
              border: "1px solid var(--od-border)",
              background: "var(--od-card-bg)",
              fontSize: 12,
              fontWeight: 500,
              color: saving ? "#cbd5e1" : "var(--od-text-secondary)",
              cursor: saving ? "default" : "pointer",
              transition: "all 0.12s",
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.background = "var(--od-surface-alt)";
                e.currentTarget.style.borderColor = "var(--od-border)";
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.currentTarget.style.background = "var(--od-card-bg)";
                e.currentTarget.style.borderColor = "var(--od-border)";
              }
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={saving || hasValidationErrors}
            title={hasValidationErrors ? "Fix validation errors before saving" : ""}
            style={{
              padding: "7px 20px",
              borderRadius: 7,
              border: "none",
              background: hasValidationErrors
                ? "#FDA4AF"
                : saveStatus === "success"
                  ? "#10B981"
                  : saving
                    ? "#93c5fd"
                    : "#3b82f6",
              fontSize: 12,
              fontWeight: 600,
              color: "#ffffff",
              cursor: saving || hasValidationErrors ? "not-allowed" : "pointer",
              transition: "all 0.12s",
              boxShadow: hasValidationErrors
                ? "none"
                : saveStatus === "success"
                  ? "0 2px 6px rgba(16,185,129,0.3)"
                  : "0 2px 6px rgba(59,130,246,0.3)",
              display: "flex",
              alignItems: "center",
              gap: 6,
              opacity: hasValidationErrors ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!saving && !hasValidationErrors)
                e.currentTarget.style.background =
                  saveStatus === "success" ? "#059669" : "#2563eb";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = hasValidationErrors
                ? "#FDA4AF"
                : saveStatus === "success"
                  ? "#10B981"
                  : saving
                    ? "#93c5fd"
                    : "#3b82f6";
            }}
          >
            {saving && !saveStatus && (
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderTopColor: "#ffffff",
                  animation: "tcSpin 0.6s linear infinite",
                }}
              />
            )}
            {saveStatus === "success" ? "✓ Saved" : saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes tcFadeIn  { 0%{opacity:0} 100%{opacity:1} }
        @keyframes tcSlideUp { 0%{opacity:0;transform:translateY(16px) scale(0.98)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes tcSpin    { to { transform: rotate(360deg) } }
        @keyframes tcErrorSlide { 0%{opacity:0;transform:translateY(-4px)} 100%{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// RecentAlerts — main component
// ═══════════════════════════════════════════════════════════════════════════════

const RecentAlerts = ({ weatherData, capitalName, stateName, weatherLoading, stateId, alwaysExpanded = false }) => {
  const [dismissed, setDismissed] = useState(new Set());
  const [configOpen, setConfigOpen] = useState(false);
  const [parameters, setParameters] = useState([]);          // current working params from API
  const [savedParameters, setSavedParameters] = useState([]); // last fetched (for reset)
  const [thresholdsLoading, setThresholdsLoading] = useState(true);
  const [isPanelOpen, setIsPanelOpen] = useState(false);     // expand/collapse state
  const bodyRef = useRef(null);

  // Alerts computed from dynamic parameters
  const alerts = useMemo(
    () => generateWeatherAlerts(weatherData, capitalName, stateName, parameters, weatherData?.currentTime),
    [weatherData, capitalName, stateName, parameters]
  );

  // After save, rebuild parameters from the updated draft
  const handleSaveThresholds = useCallback(
    (updatedDraft) => {
      const updated = parameters.map((p) => {
        const d = updatedDraft[p.parameterName] || {};
        const newHigh =
          d.high != null && d.high !== "" && !isNaN(d.high) ? d.high : p.valueHigh;
        const newLow =
          d.low != null && d.low !== "" && !isNaN(d.low) ? d.low : p.valueLow;

        return {
          ...p,
          valueHigh: newHigh,
          valueLow: newLow,
          isNumeric: newHigh !== null || newLow !== null || p.isNumeric,
        };
      });
      setParameters(updated);
      setSavedParameters(JSON.parse(JSON.stringify(updated)));
      setDismissed(new Set());
    },
    [parameters]
  );

  const visibleAlerts = alerts.filter((a) => !dismissed.has(a.parameterId));
  const activeCount = visibleAlerts.length;

  // Animate height on open/close (skipped when alwaysExpanded)
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (alwaysExpanded) {
      el.style.height = "auto";
      el.style.opacity = "1";
      el.style.overflow = "";
      return;
    }
    if (isPanelOpen) {
      el.style.overflow = "hidden";
      el.style.height = "0px";
      el.style.opacity = "0";
      const nat = el.scrollHeight || 300;
      requestAnimationFrame(() => {
        el.style.height = nat + "px";
        el.style.opacity = "1";
      });
      const t = setTimeout(() => {
        el.style.height = "auto";
        el.style.overflow = "";
      }, 380);
      return () => clearTimeout(t);
    } else {
      const cur = el.scrollHeight;
      el.style.overflow = "hidden";
      el.style.height = cur + "px";
      el.style.opacity = "1";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.height = "0px";
          el.style.opacity = "0";
        });
      });
    }
  }, [isPanelOpen, alwaysExpanded]);

  // Fetch thresholds from API on mount / stateId change
  useEffect(() => {
    if (!stateId) return;

    // Reset state immediately so stale data from previous state is cleared
    setParameters([]);
    setSavedParameters([]);
    setDismissed(new Set());
    setThresholdsLoading(true);

    let cancelled = false;

    const fetchThresholds = async () => {
      try {
        const response = await getData(`${getWeatherDataThresholds}${stateId}`);
        if (cancelled) return;

        if (response && Array.isArray(response)) {
          const built = buildParametersFromApi(response);
          setParameters(built);
          setSavedParameters(JSON.parse(JSON.stringify(built)));
        } else {
          // API returned non-array or empty — ensure clean state but show defaults
          const defaultParams = buildParametersFromApi([]);
          setParameters(defaultParams);
          setSavedParameters(JSON.parse(JSON.stringify(defaultParams)));
        }
      } catch (err) {
        console.error("Failed to fetch thresholds:", err);
        if (cancelled) return;
        const defaultParams = buildParametersFromApi([]);
        setParameters(defaultParams);
        setSavedParameters(JSON.parse(JSON.stringify(defaultParams)));
      } finally {
        if (!cancelled) {
          setThresholdsLoading(false);
        }
      }
    };

    fetchThresholds();

    // Cleanup: prevent stale responses from overwriting state
    return () => {
      cancelled = true;
    };
  }, [stateId]);

  const hasConfigured = parameters.length > 0;

  //   if (weatherLoading || thresholdsLoading) return null;

  const isLoading = weatherLoading || thresholdsLoading;

  return (
    <>
      {!isLoading && (
        <div
          style={{
            width: "100%",
            background: "var(--od-card-bg)",
            borderRadius: "0 0 10px 10px",
            borderTop:
              activeCount > 0
                ? "2px solid rgba(239,68,68,0.15)"
                : "1px solid #e5e7eb",
            padding: "12px 16px",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          {/* Header */}
          <div
            onClick={() => !alwaysExpanded && setIsPanelOpen(p => !p)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: (alwaysExpanded || isPanelOpen) && activeCount > 0 ? 10 : 0,
              transition: "margin-bottom 0.35s ease",
              cursor: alwaysExpanded ? "default" : "pointer",
              userSelect: "none",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                flexShrink: 0,
                background: activeCount > 0
                  ? "rgba(239,68,68,0.12)"
                  : "rgba(16,185,129,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                boxShadow: "none",
              }}
            >
              📢
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--od-text-primary)" }}>
              Weather Alerts
            </span>

            {activeCount > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#EF4444",
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  padding: "2px 10px",
                  borderRadius: 20,
                  letterSpacing: "0.02em",
                }}
              >
                {activeCount} active
              </span>
            )}
            {activeCount === 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#10B981",
                  background: "rgba(16,185,129,0.08)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  padding: "2px 10px",
                  borderRadius: 20,
                }}
              >
                All clear
              </span>
            )}

            {/* Config Button — hidden when alwaysExpanded */}
            {!alwaysExpanded && <button
              onClick={(e) => { e.stopPropagation(); setConfigOpen(true); }}
              title="Configure alert thresholds"
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 14px",
                borderRadius: 8,
                border: hasConfigured
                  ? "1.5px solid #3b82f6"
                  : "1px solid #d1d5db",
                background: hasConfigured
                  ? "rgba(59,130,246,0.06)"
                  : "#ffffff",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 600,
                color: hasConfigured ? "#2563eb" : "var(--od-text-secondary)",
                transition: "all 0.15s",
                boxShadow: hasConfigured
                  ? "0 0 0 2px rgba(59,130,246,0.08)"
                  : "0 1px 3px rgba(0,0,0,0.06)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = hasConfigured
                  ? "rgba(59,130,246,0.12)"
                  : "#f3f4f6";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = hasConfigured
                  ? "0 2px 8px rgba(59,130,246,0.2)"
                  : "0 2px 8px rgba(0,0,0,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = hasConfigured
                  ? "rgba(59,130,246,0.06)"
                  : "#ffffff";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = hasConfigured
                  ? "0 0 0 2px rgba(59,130,246,0.08)"
                  : "0 1px 3px rgba(0,0,0,0.06)";
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              {hasConfigured ? "Configured Thresholds" : "Configure Thresholds"}
              {hasConfigured && (
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#3b82f6",
                    flexShrink: 0,
                  }}
                />
              )}
            </button>}

            {/* Expand/Collapse Button — hidden when alwaysExpanded */}
            {!alwaysExpanded && (
            <button
              onClick={(e) => { e.stopPropagation(); setIsPanelOpen(p => !p); }}
              title={isPanelOpen ? "Collapse alerts" : "Expand alerts"}
              style={{
                width: 30,
                height: 30,
                borderRadius: 7,
                flexShrink: 0,
                background: isPanelOpen ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)",
                border: `1px solid ${isPanelOpen ? "rgba(239,68,68,0.22)" : "rgba(245,158,11,0.22)"}`,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s, border-color 0.2s",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isPanelOpen ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isPanelOpen ? "rgba(239,68,68,0.08)" : "rgba(245,158,11,0.08)";
              }}
            >
              <svg
                className={`wa-chevron-icon ${isPanelOpen ? "open" : "closed"}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M2 4.5L6 8.5L10 4.5"
                  stroke={isPanelOpen ? "#EF4444" : "#F59E0B"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            )}
          </div>

          {/* Collapsible Body */}
          <div
            ref={bodyRef}
            className="wa-body"
            style={alwaysExpanded ? { height: "auto", opacity: 1, overflow: "visible" } : { height: 0, opacity: 0, overflow: "hidden" }}
          >
            {/* No alerts */}
            {activeCount === 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 0 2px",
                  color: "var(--od-text-muted)",
                  fontSize: 12,
                }}
              >
                <span style={{ fontSize: 16 }}>✅</span>
                {hasConfigured
                  ? "No weather parameters are currently exceeding alert thresholds."
                  : 'No thresholds configured. Click "Configure Thresholds" to set alerts.'}
              </div>
            )}

            {/* Alert Cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {visibleAlerts.map((alert) => {
                const badge =
                  SEVERITY_BADGE_CONFIG[alert.severity] || SEVERITY_BADGE_CONFIG.moderate;
                return (
                  <div
                    key={alert.parameterId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      background:
                        alert.severity === "extreme"
                          ? "rgba(254,226,226,0.55)"
                          : alert.severity === "high"
                            ? "rgba(255,237,213,0.5)"
                            : "rgba(254,249,195,0.4)",
                      border: `1px solid ${alert.border}`,
                      borderRadius: 10,
                      transition: "all 0.2s ease",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        background: alert.severityColor,
                        borderRadius: "10px 0 0 10px",
                      }}
                    />
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background:
                          alert.severity === "extreme"
                            ? "rgba(254,226,226,0.8)"
                            : alert.severity === "high"
                              ? "rgba(255,237,213,0.8)"
                              : "rgba(254,249,195,0.7)",
                        border: `1px solid ${alert.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      {alert.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: 15, fontWeight: 800, color: "var(--od-text-primary)", marginRight: 4, whiteSpace: "nowrap" }}>
                          {alert.label}
                        </span>

                        {/* Message */}
                        <div style={{ fontSize: 13, color: "var(--od-text-secondary)", fontWeight: 500, lineHeight: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
                          <span style={{ color: "var(--od-text-primary)" }}>
                            <b>{alert.parameterName}</b> has crossed the threshold (Current: <b style={{ color: alert.severityColor }}>{alert.currentValue}</b>, Threshold: <b>{alert.thresholdValue}</b>) at <b>{alert.location}</b> on <b>{alert.time}</b>
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        setDismissed((prev) => new Set([...prev, alert.parameterId]))
                      }
                      title="Dismiss this alert"
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        color: "var(--od-text-muted)",
                        flexShrink: 0,
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--od-text-muted)")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--od-text-muted)")}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <style>{`
          @keyframes alertBadgePulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.85; transform: scale(1.04); }
          }
          .wa-body {
            transition: height 0.35s cubic-bezier(0.4,0,0.2,1),
                        opacity 0.3s ease;
          }
          .wa-chevron-icon {
            transition: transform 0.35s cubic-bezier(0.4,0,0.2,1);
          }
          .wa-chevron-icon.open  { transform: rotate(180deg); }
          .wa-chevron-icon.closed{ transform: rotate(0deg); }
        `}</style>
        </div>
      )}

      {/* Modal is ALWAYS rendered so configOpen state survives loading cycles */}
      <ThresholdConfigModal
        isOpen={configOpen}
        onClose={() => setConfigOpen(false)}
        parameters={parameters}
        savedParameters={savedParameters}
        onSave={handleSaveThresholds}
        weatherData={weatherData}
        stateId={stateId}
      />
    </>
  );
};

export default RecentAlerts;